import { FormattedMessage, useIntl } from 'react-intl';

import styles from '@components/CareNeedTags.module.css';
import Tag from '@components/labels/Tag';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import { CareProfileCareNeedEnum } from '@typing/Generated';

type CareNeedTagProps = {
  careNeed: CareProfileCareNeedEnum;
  onToggleSelected: (careNeed: CareProfileCareNeedEnum) => void;
  selectedOptions: CareProfileCareNeedEnum[];
};

const CareNeedTag = ({ careNeed, onToggleSelected, selectedOptions }: CareNeedTagProps) => {
  const intl = useIntl();

  return (
    <div className={styles.careNeedsCategoryTagWrapper} onClick={() => onToggleSelected(careNeed)}>
      <Tag
        color={selectedOptions.includes(careNeed) ? 'secondary500' : 'neutral50'}
        icon={selectedOptions.includes(careNeed) ? 'checkmark' : 'plus'}
        text={intl.formatMessage({
          id: `models.careProfile.questions.careNeeds.options.${careNeed}.content`
        })}
      />
    </div>
  );
};

type Props = {
  onCareNeedToggle: (careNeed: CareProfileCareNeedEnum) => void;
  selectedCareNeeds: CareProfileCareNeedEnum[];
};

const CareNeedTags = ({ onCareNeedToggle, selectedCareNeeds }: Props) => {
  const categoryData: { [key: string]: CareProfileCareNeedEnum[] } = {
    medicalCare: [
      CareProfileCareNeedEnum.SCHEDULING_APPOINTMENTS,
      CareProfileCareNeedEnum.INSURANCE_DENIALS,
      CareProfileCareNeedEnum.SECOND_OPINIONS
    ],
    nonMedicalCare: [
      CareProfileCareNeedEnum.TRANSPORTATION,
      CareProfileCareNeedEnum.MEAL_DELIVERY,
      CareProfileCareNeedEnum.EMERGENCY_RESPONSE_SYSTEMS,
      CareProfileCareNeedEnum.IN_HOME_AID
    ],
    // eslint-disable-next-line sort-keys
    mentalHealthCare: [
      CareProfileCareNeedEnum.CRISIS_SUPPORT,
      CareProfileCareNeedEnum.AFFORDABLE_THERAPIST,
      CareProfileCareNeedEnum.PSYCHIATRIC_HOSPITALIZATION
    ],
    // eslint-disable-next-line sort-keys
    housing: [
      CareProfileCareNeedEnum.HOUSING_OPTIONS,
      CareProfileCareNeedEnum.RELOCATION,
      CareProfileCareNeedEnum.ASSISTED_LIVING
    ],
    // eslint-disable-next-line sort-keys
    education: [
      CareProfileCareNeedEnum.AFTER_SCHOOL_CARE,
      CareProfileCareNeedEnum.EDUCATION_ACCOMODATIONS,
      CareProfileCareNeedEnum.DEVELOPMENTAL_EVALUATION
    ],
    legalNeeds: [
      CareProfileCareNeedEnum.LEGAL_DOCUMENTS,
      CareProfileCareNeedEnum.FIND_A_LAWYER,
      CareProfileCareNeedEnum.IMMIGRATION_ASSISTANCE
    ],
    // eslint-disable-next-line sort-keys
    finances: [CareProfileCareNeedEnum.BUDGETING, CareProfileCareNeedEnum.FINANCIAL_PLANNING],
    futurePlanning: [CareProfileCareNeedEnum.LONG_TERM_CARE, CareProfileCareNeedEnum.FAMILY_MEDIATION]
  };

  return (
    <div>
      {Object.keys(categoryData).map(categoryName => (
        <div className={styles.careNeedsCategory} key={categoryName}>
          <div className={styles.careNeedsCategoryName}>
            <BodyCopyBold>
              <FormattedMessage id={`forms.onboarding.careNeeds.categories.${categoryName}`} />
            </BodyCopyBold>
          </div>
          <div className={styles.careNeedsTags}>
            {categoryData[categoryName].map((careNeed: CareProfileCareNeedEnum) => (
              <CareNeedTag
                careNeed={careNeed}
                key={careNeed}
                onToggleSelected={onCareNeedToggle}
                selectedOptions={selectedCareNeeds}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CareNeedTags;
